import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { assetApi, accountApi, clientApi } from '@/services/api'
import type { FixedAsset, Account } from '@/types'
import { Boxes, Plus, X, PlayCircle, Archive } from 'lucide-react'
import toast from 'react-hot-toast'
import { formatCurrency } from '@/utils/currency'
import { Button } from '@/components/ui/button'

const DEP_METHODS = [
  { value: 'straight_line', label: 'Straight Line' },
  { value: 'declining_balance', label: 'Declining Balance' },
  { value: 'none', label: 'None (Non-Depreciable)' },
]

function DepreciationModal({ asset, currency, onClose, onDone }: { asset: FixedAsset; currency: string; onClose: () => void; onDone: (a: FixedAsset) => void }) {
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10))
  const [months, setMonths] = useState(1)
  const [running, setRunning] = useState(false)

  async function run() {
    setRunning(true)
    try {
      const res = await assetApi.runDepreciation(asset.id, { date, months })
      onDone(res.data.asset)
      toast.success('Depreciation journal posted')
      onClose()
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed to post depreciation'
      toast.error(msg)
    } finally {
      setRunning(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-modal w-full max-w-sm">
        <div className="flex items-center justify-between p-5 border-b border-surface-100">
          <h2 className="text-[13px] font-bold text-[#000A1E]">Run Depreciation — {asset.name}</h2>
          <button onClick={onClose} className="btn-ghost p-1"><X className="w-4 h-4" /></button>
        </div>
        <div className="p-5 space-y-4">
          <p className="text-[13px] text-[rgba(0,10,30,0.75)]">
            Book value: <strong className="text-[#000A1E]">{formatCurrency(asset.book_value, currency)}</strong>
          </p>
          <div>
            <label className="input-label">Date</label>
            <input type="date" value={date} onChange={(e) => setDate(e.target.value)} className="input" />
          </div>
          <div>
            <label className="input-label">Months</label>
            <input type="number" min={1} max={12} value={months} onChange={(e) => setMonths(Number(e.target.value))} className="input" />
          </div>
        </div>
        <div className="flex gap-3 p-5 border-t border-surface-100">
          <Button variant="default" onClick={run} disabled={running} className="flex-1">
            {running ? 'Posting...' : 'Post Depreciation'}
          </Button>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
        </div>
      </div>
    </div>
  )
}

function CreateAssetModal({ clientId, accounts, onClose, onCreated }: {
  clientId: string
  accounts: Account[]
  onClose: () => void
  onCreated: (a: FixedAsset) => void
}) {
  const [form, setForm] = useState({
    name: '', asset_number: '', description: '',
    asset_account_id: '', accumulated_depreciation_account_id: '',
    depreciation_expense_account_id: '',
    cost: '', purchase_date: new Date().toISOString().slice(0, 10),
    useful_life_months: '60', depreciation_method: 'straight_line', residual_value: '0',
  })
  const [saving, setSaving] = useState(false)

  const assetAccounts = accounts.filter((a) => a.account_type === 'asset')

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setSaving(true)
    try {
      const res = await assetApi.create({
        client_ledger_id: clientId,
        ...form,
        accumulated_depreciation_account_id: form.accumulated_depreciation_account_id || null,
        depreciation_expense_account_id: form.depreciation_expense_account_id || null,
      })
      onCreated(res.data.asset)
      toast.success('Asset created')
      onClose()
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed to create asset'
      toast.error(msg)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-modal w-full max-w-lg overflow-y-auto max-h-[90vh]">
        <div className="flex items-center justify-between p-5 border-b border-surface-100">
          <h2 className="text-[13px] font-bold text-[#000A1E]">Add Fixed Asset</h2>
          <button onClick={onClose} className="btn-ghost p-1"><X className="w-4 h-4" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="input-label">Asset Name *</label>
              <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input" required />
            </div>
            <div>
              <label className="input-label">Asset Number</label>
              <input value={form.asset_number} onChange={(e) => setForm({ ...form, asset_number: e.target.value })} className="input" placeholder="Optional" />
            </div>
            <div>
              <label className="input-label">Purchase Date *</label>
              <input type="date" value={form.purchase_date} onChange={(e) => setForm({ ...form, purchase_date: e.target.value })} className="input" required />
            </div>
            <div>
              <label className="input-label">Cost *</label>
              <input type="number" step="0.01" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} className="input" required />
            </div>
            <div>
              <label className="input-label">Residual Value</label>
              <input type="number" step="0.01" value={form.residual_value} onChange={(e) => setForm({ ...form, residual_value: e.target.value })} className="input" />
            </div>
            <div className="col-span-2">
              <label className="input-label">Asset Account *</label>
              <select value={form.asset_account_id} onChange={(e) => setForm({ ...form, asset_account_id: e.target.value })} className="input" required>
                <option value="">Select account...</option>
                {assetAccounts.map((a) => <option key={a.id} value={a.id}>{a.code} — {a.name}</option>)}
              </select>
            </div>
            <div>
              <label className="input-label">Depreciation Method</label>
              <select value={form.depreciation_method} onChange={(e) => setForm({ ...form, depreciation_method: e.target.value })} className="input">
                {DEP_METHODS.map((m) => <option key={m.value} value={m.value}>{m.label}</option>)}
              </select>
            </div>
            <div>
              <label className="input-label">Useful Life (months)</label>
              <input type="number" value={form.useful_life_months} onChange={(e) => setForm({ ...form, useful_life_months: e.target.value })} className="input" />
            </div>
            {form.depreciation_method !== 'none' && (
              <>
                <div>
                  <label className="input-label">Accum. Depreciation Account</label>
                  <select value={form.accumulated_depreciation_account_id} onChange={(e) => setForm({ ...form, accumulated_depreciation_account_id: e.target.value })} className="input">
                    <option value="">Select...</option>
                    {assetAccounts.map((a) => <option key={a.id} value={a.id}>{a.code} — {a.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="input-label">Depreciation Expense Account</label>
                  <select value={form.depreciation_expense_account_id} onChange={(e) => setForm({ ...form, depreciation_expense_account_id: e.target.value })} className="input">
                    <option value="">Select...</option>
                    {accounts.filter((a) => a.account_type === 'expense').map((a) => <option key={a.id} value={a.id}>{a.code} — {a.name}</option>)}
                  </select>
                </div>
              </>
            )}
          </div>
          <div className="flex gap-3 pt-2 border-t border-surface-100">
            <Button type="submit" variant="create" disabled={saving}>{saving ? 'Creating...' : 'Create Asset'}</Button>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default function AssetRegister() {
  const { clientId } = useParams<{ clientId: string }>()
  const navigate = useNavigate()
  const [assets, setAssets] = useState<FixedAsset[]>([])
  const [accounts, setAccounts] = useState<Account[]>([])
  const [loading, setLoading] = useState(true)
  const [showCreate, setShowCreate] = useState(false)
  const [depAsset, setDepAsset] = useState<FixedAsset | null>(null)
  const [currency, setCurrency] = useState('USD')
  const [premiumGated, setPremiumGated] = useState(false)

  useEffect(() => {
    if (!clientId) return
    clientApi.get(clientId).then((r) => setCurrency(r.data.client.currency)).catch(() => {})
    Promise.all([
      assetApi.list(clientId),
      accountApi.list(clientId),
    ]).then(([assetRes, accRes]) => {
      setAssets(assetRes.data.assets)
      setAccounts(accRes.data.accounts)
    }).catch((err: unknown) => {
      const status = (err as { response?: { status?: number } })?.response?.status
      if (status === 402) setPremiumGated(true)
      else toast.error('Failed to load assets')
    })
      .finally(() => setLoading(false))
  }, [clientId])

  async function handleDispose(asset: FixedAsset) {
    if (!confirm(`Dispose "${asset.name}"?`)) return
    try {
      const today = new Date().toISOString().slice(0, 10)
      const res = await assetApi.dispose(asset.id, today)
      setAssets((prev) => prev.filter((a) => a.id !== res.data.asset.id))
      toast.success('Asset disposed')
    } catch {
      toast.error('Failed to dispose asset')
    }
  }

  if (premiumGated) {
    return (
      <div className="max-w-sm mx-auto mt-16">
        <div className="card p-10 text-center">
          <div className="w-12 h-12 bg-amber-50 rounded-xl flex items-center justify-center mx-auto mb-4">
            <Boxes className="w-6 h-6 text-amber-500" />
          </div>
          <h2 className="text-[21px] font-bold text-[#000A1E] leading-[32px] mb-2">Premium Feature</h2>
          <p className="text-[13px] text-[rgba(0,10,30,0.75)] mb-5">
            Fixed assets require a Premium client. Upgrade in Client Settings &rarr; Plan.
          </p>
          <Button variant="default" onClick={() => navigate(`/clients/${clientId}/settings`)}>
            Upgrade to Premium
          </Button>
        </div>
      </div>
    )
  }

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <div className="flex-1">
          <h1 className="text-[17px] font-bold text-[#000A1E] leading-[24px]">Asset Register</h1>
          <p className="text-[13px] text-[rgba(0,10,30,0.75)] mt-0.5">Fixed assets and depreciation</p>
        </div>
        <Button variant="create" onClick={() => setShowCreate(true)}>
          <Plus className="w-4 h-4" /> Add Asset
        </Button>
      </div>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="w-5 h-5 border-2 border-[#0078C8] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="card overflow-hidden">
          <table className="w-full">
            <thead className="border-b border-surface-100">
              <tr>
                {['Asset', 'Account', 'Cost', 'Acc. Depreciation', 'Book Value', 'Method', ''].map((h) => (
                  <th key={h} className="px-4 py-2.5 text-left text-[13px] font-bold text-[rgba(0,10,30,0.75)] uppercase tracking-wider">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-surface-50">
              {assets.length === 0 ? (
                <tr><td colSpan={7} className="px-4 py-10 text-center text-[13px] text-[#CCCED2]">No assets yet</td></tr>
              ) : (
                assets.map((asset) => (
                  <tr key={asset.id} className="hover:bg-surface-50">
                    <td className="px-4 py-3">
                      <p className="text-[13px] font-medium text-[#000A1E]">{asset.name}</p>
                      {asset.asset_number && <p className="text-[13px] text-[#CCCED2]">#{asset.asset_number}</p>}
                    </td>
                    <td className="px-4 py-3 text-[13px] text-[rgba(0,10,30,0.75)]">{asset.asset_account_name}</td>
                    <td className="px-4 py-3 text-[13px] text-right text-[#000A1E]">{formatCurrency(asset.cost, currency)}</td>
                    <td className="px-4 py-3 text-[13px] text-right text-red-500">{formatCurrency(asset.accumulated_depreciation, currency)}</td>
                    <td className="px-4 py-3 text-[13px] text-right font-medium text-[#000A1E]">{formatCurrency(asset.book_value, currency)}</td>
                    <td className="px-4 py-3 text-[13px] text-[rgba(0,10,30,0.75)]">
                      {DEP_METHODS.find((m) => m.value === asset.depreciation_method)?.label || asset.depreciation_method}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-1">
                        {asset.depreciation_method !== 'none' && (
                          <button
                            onClick={() => setDepAsset(asset)}
                            className="btn-ghost p-1.5 text-[#0078C8]"
                            title="Run depreciation"
                          >
                            <PlayCircle className="w-3.5 h-3.5" />
                          </button>
                        )}
                        <button
                          onClick={() => handleDispose(asset)}
                          className="btn-ghost p-1.5 text-[#CCCED2] hover:text-red-500"
                          title="Dispose asset"
                        >
                          <Archive className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      )}

      {showCreate && clientId && (
        <CreateAssetModal
          clientId={clientId}
          accounts={accounts}
          onClose={() => setShowCreate(false)}
          onCreated={(a) => setAssets((prev) => [a, ...prev])}
        />
      )}
      {depAsset && (
        <DepreciationModal
          asset={depAsset}
          currency={currency}
          onClose={() => setDepAsset(null)}
          onDone={(updated) => setAssets((prev) => prev.map((a) => a.id === updated.id ? updated : a))}
        />
      )}
    </div>
  )
}
